import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import BullhornNotePromptDialog from '../components/BullhornNotePromptDialog';
import { useBullhornNoteHandler } from '../hooks/useBullhornNoteHandler';
import { toast, Toaster } from 'sonner';
import { 
  FileText, 
  Send, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Eye,
  RefreshCw,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  User,
  Building2,
  Mail,
  FileCheck,
  Ban,
  Package,
  ClipboardEdit,
  Layers,
  Copy as CopyIcon,
  ExternalLink,
  BellRing,
  ShieldCheck,
  Shield,
  ShieldPlus,
  Search,
  Files,
  KeyRound,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status configuration
const STATUS_CONFIG = {
  sent: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Sent' },
  opened: { icon: Eye, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Opened' },
  in_progress: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-100', label: 'In Progress' },
  completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Completed' },
  pending_approval: { icon: FileCheck, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Pending Approval' },
  approved: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Approved' },
  expired: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Expired' },
  cancelled: { icon: Ban, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Cancelled' },
};

// Entity type colors matching Bullhorn brand
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', border: 'border-[#4CAF50]', label: 'Candidate' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', border: 'border-[#F9A825]', label: 'Contact' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', border: 'border-[#1a4a5e]', label: 'Placement' },
};

// Configure axios for embed
const embedApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Inline Login Component for iframe
function InlineLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const token = response.data.access_token;
      // Store in sessionStorage for this iframe session
      sessionStorage.setItem('embed_token', token);
      onLoginSuccess(token);
    } catch (err) {
      // Handle different error formats
      const errorData = err.response?.data;
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <FileText className="w-6 h-6 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">HyreDocs</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to view documents</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-orange-500 hover:bg-orange-600"
            disabled={loading}
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <User className="w-4 h-4 mr-2" />
            )}
            Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function BullhornEmbedPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [entityInfo, setEntityInfo] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedPackets, setSelectedPackets] = useState([]);
  const toggleTemplate = (id) => setSelectedTemplates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const togglePacket = (id) => setSelectedPackets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  // Live search filters for the in-dialog template / packet / form
  // pickers. The Bullhorn embed iframe is the highest-volume surface
  // for our power users — a searchable list with keyboard scan beats
  // a 100-item native <select>.
  const [templateSearch, setTemplateSearch] = useState('');
  const [packetSearch, setPacketSearch] = useState('');
  const [formSearch, setFormSearch] = useState('');
  const [sendMode, setSendMode] = useState('document'); // 'document' | 'packet' | 'form'
  const [sending, setSending] = useState(false);
  // Forms state for the Form tab
  const [forms, setForms] = useState([]);
  const [formsEnabled, setFormsEnabled] = useState(false);
  const [selectedForm, setSelectedForm] = useState('');
  // Post-send "add a Bullhorn note?" prompt state
  // Single source of truth for the post-send Bullhorn note behaviour.
  // Mode (popup / toast / silent / off) + the default body/action come
  // from tenant settings. See useBullhornNoteHandler for the dispatch
  // logic + graceful fallback to popup on auto-log failure.
  const bullhornNote = useBullhornNoteHandler(embedApi);
  const [authToken, setAuthToken] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  // Polish-pass state — recruiter-facing list filtering and bulk reminder.
  // `statusFilter` shrinks the visible list (mirrors the chips above it).
  // `bulkReminding` disables the header button while a bulk reminder is in
  // flight so a recruiter can't double-fire emails on a slow connection.
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkReminding, setBulkReminding] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  // For placement entities: which side of the placement the recruiter
  // wants to send to. 'candidate' (worker), 'contact' (client contact),
  // or 'both' (one document to each). Defaults to candidate to match the
  // existing behavior. The Bullhorn entity lookup now returns both sides
  // so the toggle can pre-fill names + emails without an extra fetch.
  const [placementRecipientType, setPlacementRecipientType] = useState('candidate');

  // ── Credas state ──────────────────────────────────────────────────
  // Only shown when the tenant has Credas configured + enabled in
  // Settings → Integrations. Polled once on mount; we hide the button
  // entirely when the feature flag is off so recruiters who don't use
  // Credas see no extra UI.
  const [credasEnabled, setCredasEnabled] = useState(false);
  const [credasState, setCredasState] = useState(null);  // { magic_link, status, ... }
  const [credasSending, setCredasSending] = useState(false);

  // ── Credential Request state ───────────────────────────────────────
  // Separate dialog from the Send Document flow because the candidate
  // input is fundamentally different (no template, no fields — just an
  // upload checklist tied to Bullhorn certification categories).
  const [showCredentialDialog, setShowCredentialDialog] = useState(false);
  const [credentialCategories, setCredentialCategories] = useState([]);
  const [credentialCategoriesLoading, setCredentialCategoriesLoading] = useState(false);
  const [credentialSearch, setCredentialSearch] = useState('');
  const [selectedCredentialIds, setSelectedCredentialIds] = useState([]); // array of bullhorn category ids
  const [credentialCustomMessage, setCredentialCustomMessage] = useState('');
  const [credentialRequiredMode, setCredentialRequiredMode] = useState('all'); // 'all' | 'optional'
  const [credentialSending, setCredentialSending] = useState(false);

  // Get parameters from URL
  const entityId = searchParams.get('entityId') || searchParams.get('id');
  const entityType = searchParams.get('entityType') || searchParams.get('type') || 'candidate';
  const token = searchParams.get('token'); // Optional auth token passed from Bullhorn
  
  // SSO parameters
  const ssoEmail = searchParams.get('ssoEmail');
  const ssoTimestamp = searchParams.get('ssoTimestamp');
  const ssoSignature = searchParams.get('ssoSignature');
  const autoLogin = searchParams.get('autoLogin') === 'true';
  // Set by the backend when the SSO redirect could identify the tenant but
  // NOT the specific Bullhorn user (UserID lookup failed). We refuse to
  // silently impersonate the first admin in that case — surfacing this
  // flag here so the UI can render an actionable "not provisioned" state.
  const ssoError = searchParams.get('ssoError');
  const ssoErrorUserId = searchParams.get('ssoUserId');

  // Handle SSO login
  const handleSSOLogin = useCallback(async () => {
    if (!ssoEmail || !ssoTimestamp || !ssoSignature) return false;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/sso/bullhorn`, {
        email: ssoEmail,
        timestamp: parseInt(ssoTimestamp),
        signature: ssoSignature,
      });
      
      const newToken = response.data.access_token;
      sessionStorage.setItem('embed_token', newToken);
      sessionStorage.setItem('sso_user', JSON.stringify(response.data.user));
      setAuthToken(newToken);
      embedApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return true;
    } catch (err) {
      console.error('SSO login failed:', err);
      // Don't show error - fall back to manual login
      return false;
    }
  }, [ssoEmail, ssoTimestamp, ssoSignature]);

  // Set up auth token - try cookie (autoLogin), then SSO, then sessionStorage, then URL param
  useEffect(() => {
    const initAuth = async () => {
      // First try cookie-based auth (from SSO redirect)
      if (autoLogin) {
        try {
          const meResponse = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          if (meResponse.data?.id) {
            setAuthToken('cookie');
            embedApi.defaults.withCredentials = true;
            return;
          }
        } catch (e) {
          // Cookie auth probe failed — fall through to SSO / form login below.
        }
      }
      
      // Then try SSO if parameters are present
      if (ssoEmail && ssoTimestamp && ssoSignature) {
        const ssoSuccess = await handleSSOLogin();
        if (ssoSuccess) return;
      }
      
      // Then try stored token
      const storedToken = sessionStorage.getItem('embed_token');
      if (token) {
        setAuthToken(token);
        sessionStorage.setItem('embed_token', token);
        embedApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else if (storedToken) {
        setAuthToken(storedToken);
        embedApi.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
    };
    
    initAuth();
  }, [token, ssoEmail, ssoTimestamp, ssoSignature, handleSSOLogin, autoLogin]);

  // Handle login success from inline form
  const handleLoginSuccess = (newToken) => {
    setAuthToken(newToken);
    embedApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Pre-fill recipient email when entity info loads. For placements we
  // switch the email source based on the selected recipient side
  // (candidate vs contact). "Both" uses the candidate's email as the
  // primary display; the send action expands into two POSTs.
  useEffect(() => {
    if (!entityInfo) return;
    if (entityType === 'placement') {
      if (placementRecipientType === 'contact') {
        setRecipientEmail(entityInfo.contact?.email || '');
      } else {
        // candidate or both
        setRecipientEmail(entityInfo.candidate?.email || entityInfo.email || '');
      }
    } else if (entityInfo.email) {
      setRecipientEmail(entityInfo.email);
    }
  }, [entityInfo, entityType, placementRecipientType]);

  // Also pre-fill email when Send dialog opens
  useEffect(() => {
    if (!showSendDialog || !entityInfo) return;
    if (recipientEmail) return;
    if (entityType === 'placement' && placementRecipientType === 'contact') {
      setRecipientEmail(entityInfo.contact?.email || '');
    } else if (entityInfo.email) {
      setRecipientEmail(entityInfo.email);
    }
  }, [showSendDialog, entityInfo, entityType, placementRecipientType, recipientEmail]);

  // Load data
  const loadData = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch documents for this entity
      const docsResponse = await embedApi.get(`/api/documents/by-bullhorn-id/${entityId}`, {
        params: { entity_type: entityType }
      });
      // Fetch credential requests filed against this Bullhorn entity
      // and merge them into the same list as documents — same row UI,
      // marked with a `kind: 'credential_request'` discriminator and a
      // shield icon. Failures are non-fatal (route may be missing on
      // older deployments).
      let credentialRequests = [];
      try {
        const crResp = await embedApi.get(
          `/api/credential-requests/by-bullhorn-id/${entityId}`,
          { params: { entity_type: entityType } }
        );
        credentialRequests = (crResp.data?.requests || []).map(r => ({
          ...r,
          kind: 'credential_request',
          // Project credential-request fields onto the document row shape
          // so existing chips / filters / expanded UI work without forks.
          template_name: `Credentials: ${(r.items || []).map(i => i.bullhorn_category_name).slice(0, 3).join(', ')}${(r.items || []).length > 3 ? ` +${r.items.length - 3}` : ''}`,
          candidate_email: r.candidate_email,
          candidate_name: r.candidate_name,
          sent_at: r.sent_at || r.created_at,
          completed_at: r.completed_at,
          share_url: r.short_url,
        }));
      } catch (e) {
        // Quietly ignore if endpoint isn't available yet.
      }

      const docs = docsResponse.data.documents || [];
      const merged = [...credentialRequests, ...docs].sort((a, b) => {
        const ta = new Date(a.sent_at || a.created_at || 0).getTime();
        const tb = new Date(b.sent_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setDocuments(merged);
      setEntityInfo(docsResponse.data.entity || null);
      
      // Auto-populate email from entity info
      if (docsResponse.data.entity?.email) {
        setRecipientEmail(docsResponse.data.entity.email);
      }

      // Fetch templates
      const templatesResponse = await embedApi.get('/api/templates');
      setTemplates(templatesResponse.data || []);

      // Fetch packets
      const packetsResponse = await embedApi.get('/api/packets');
      setPackets(packetsResponse.data || []);

      // Fetch forms — feature-flagged, so 403 is the normal "off" path
      // and we just hide the Form tab. Filter to the entity type so the
      // recruiter only sees forms targeted at the record they're on.
      try {
        const formsResponse = await embedApi.get('/api/forms');
        const allForms = (formsResponse.data || []).filter(f => f.status !== 'archived');
        const relevant = allForms.filter(
          f => f.entity_type === entityType || !f.entity_type
        );
        setForms(relevant);
        setFormsEnabled(true);
      } catch (formsErr) {
        if (formsErr.response?.status === 403) {
          setForms([]);
          setFormsEnabled(false);
        } else {
          // Surface unexpected errors but never break the doc/packet tabs.
          console.warn('Form list fetch failed:', formsErr);
        }
      }
      // ── Credas integration check ─────────────────────────────────
      // Shown only when this tenant has the integration enabled. We
      // also fetch any existing Credas state for this candidate so
      // recruiters who re-open the tab see a "link already generated"
      // pill instead of accidentally creating a duplicate process.
      try {
        const credasCfg = await embedApi.get('/api/credas/config');
        const enabled = !!credasCfg.data?.enabled && !!credasCfg.data?.api_key_set;
        setCredasEnabled(enabled);
        if (enabled) {
          const candEmail = docsResponse.data.entity?.candidate?.email
            || docsResponse.data.entity?.email
            || '';
          if (candEmail) {
            try {
              const credasResp = await embedApi.get('/api/credas/candidate', { params: { email: candEmail } });
              if (credasResp.data?.status && credasResp.data.status !== 'not_started') {
                setCredasState(credasResp.data);
              }
            } catch (e) { /* no prior state — fine */ }
          }
        }
      } catch (e) {
        setCredasEnabled(false);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in to HyreDocs.');
      }
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    if (authToken) {
      loadData();
      // Proactively ensure the tenant's Bullhorn session is alive for every
      // custom-tab iframe load — the user shouldn't need to visit the main
      // app's Settings page to wake the API back up.
      embedApi.post('/api/tenants/me/bullhorn/ensure-connection').catch(e => console.warn('Bullhorn ensure-connection:', e?.message));
    } else {
      setLoading(false);
    }
  }, [authToken, loadData]);

  // ── Send the candidate to Credas (creates the no-contact process
  // and resolves the magic link, which we display + copy-to-clipboard
  // so the recruiter can paste it anywhere or trust the email merge
  // tag to embed it on subsequent packet sends).
  const handleSendCredas = async () => {
    if (!entityInfo) return;
    const cand = entityInfo.candidate || entityInfo;
    const email = cand?.email;
    if (!email) {
      toast.error('No candidate email on this record — cannot send to Credas.');
      return;
    }
    const fullName = (cand?.name || `${cand?.first_name || ''} ${cand?.last_name || ''}`).trim();
    const parts = fullName.split(/\s+/);
    const firstName = cand?.first_name || parts[0] || '';
    const lastName = cand?.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : '(N/A)');

    setCredasSending(true);
    try {
      const { data } = await embedApi.post('/api/credas/initiate', {
        candidate_email: email,
        first_name: firstName,
        last_name: lastName,
        phone: cand?.phone || cand?.mobile,
        candidate_bullhorn_id: cand?.bullhorn_id || entityId,
      });
      setCredasState(data);
      if (data?.status === 'credas_failed') {
        toast.error(`Credas failed: ${data.last_error || 'unknown error'}`);
      } else if (data?.magic_link) {
        toast.success('Credas link generated — copy it from the badge.');
      } else {
        toast.success('Credas process created (link still resolving).');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not contact Credas.');
    } finally {
      setCredasSending(false);
    }
  };

  const handleSendDocument = async () => {
    if (selectedTemplates.length === 0 || !entityId) return;
    // For placement entities, build the recipient list based on the
    // chosen side. "Both" expands into two sends — one to each.
    let recipientList;
    if (entityType === 'placement') {
      const candEmail = entityInfo?.candidate?.email || entityInfo?.email || '';
      const candName = entityInfo?.candidate?.name || entityInfo?.name || 'Candidate';
      const contactEmail = entityInfo?.contact?.email || '';
      const contactName = entityInfo?.contact?.name || 'Contact';
      if (placementRecipientType === 'both') {
        recipientList = [];
        if (candEmail) recipientList.push({ side: 'candidate', name: candName, email: candEmail });
        if (contactEmail) recipientList.push({ side: 'contact', name: contactName, email: contactEmail });
        if (recipientList.length === 0) {
          toast.error('Neither candidate nor contact has an email in Bullhorn — add one first.');
          return;
        }
      } else if (placementRecipientType === 'contact') {
        if (!contactEmail) {
          toast.error('The linked client contact has no email in Bullhorn.');
          return;
        }
        recipientList = [{ side: 'contact', name: contactName, email: contactEmail }];
      } else {
        // candidate
        const useEmail = recipientEmail || candEmail;
        if (!useEmail) {
          toast.error('The candidate has no email in Bullhorn — enter one above.');
          return;
        }
        recipientList = [{ side: 'candidate', name: candName, email: useEmail }];
      }
    } else {
      if (!recipientEmail) return;
      recipientList = [{ side: entityType, name: entityInfo?.name || 'Unknown', email: recipientEmail }];
    }

    setSending(true);
    try {
      let lastResp = null;
      let ok = 0;
      const failed = [];
      for (const templateId of selectedTemplates) {
        for (const r of recipientList) {
          try {
            const resp = await embedApi.post('/api/documents/send', {
              template_id: templateId,
              recipient: {
                entity_type: entityType,
                bullhorn_id: entityId,
                name: r.name,
                email: r.email,
                ...(entityType === 'placement' ? { placement_recipient_type: r.side } : {}),
              },
              message: customMessage || undefined,
            });
            lastResp = resp;
            ok += 1;
          } catch (err) {
            failed.push({ side: r.side, email: r.email, templateId });
          }
        }
      }

      if (ok > 0 && failed.length === 0) {
        toast.success(ok === 1 ? 'Document sent successfully!' : `Sent ${ok} documents (candidate + contact)`);
      } else if (ok > 0 && failed.length > 0) {
        toast.warning(`Sent ${ok}, ${failed.length} failed (${failed.map(f => f.side).join(', ')})`);
      } else {
        throw failed[0] && failed[0].err ? failed[0].err : new Error('Send failed');
      }

      setShowSendDialog(false);
      setSelectedTemplates([]);
      setCustomMessage('');
      setEditingEmail(false);
      loadData(); // Refresh the list

      // Prompt the recruiter to log a note against the Bullhorn record.
      const tpl = templates.find((t) => t.id === selectedTemplates[0]);
      const recipientLabel = recipientList.map(r => `${r.name} <${r.email}>`).join(', ');
      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: lastResp?.data?.document_id || null,
        context: {
          template_name: tpl?.name || 'document',
          document_type: tpl?.name || 'document',
          form_name: null,
          recipient_name: recipientList.map(r => r.name).join(', '),
          recipient_email: recipientList.map(r => r.email).join(', '),
          sender_name: '', // Embed has no logged-in user context — sender_name left blank.
        },
      });
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send document';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        // Pydantic validation errors
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendPacket = async () => {
    if (selectedPackets.length === 0 || !entityId || !recipientEmail) return;

    setSending(true);
    try {
      let totalDocs = 0;
      let ok = 0;
      const failed = [];

      for (const packetId of selectedPackets) {
        const packetInfo = packets.find(p => p.id === packetId);
      const templatesInPacket = packetInfo?.templates || [];
      const hasCandidateDocs = templatesInPacket.some(
        (t) => t.send_to_candidate !== false,
      );
      const hasContactDocs = templatesInPacket.some(
        (t) => !!t.send_to_contact,
      );

      // When the packet contains BOTH candidate-typed and contact-typed
      // templates, route each side automatically: candidate-docs to the
      // candidate, contact-docs to the client contact. Requires the
      // current Bullhorn record to expose both parties — only true for
      // placement entities. From candidate/contact-only views the
      // mixed-typed contact half can't be routed, so we fall back to the
      // single-recipient flow and the backend's existing entity-type
      // mismatch handling.
      const candEmail = entityInfo?.candidate?.email;
      const contactEmail = entityInfo?.contact?.email;
      const hasPlacementContext =
        entityType === 'placement' && candEmail && contactEmail;

      // Respect the user's "Send to" selection (Candidate / Contact / Both)
      // Only use full routing (both recipients) when "Both" is explicitly selected
      const useRouting =
        hasPlacementContext && placementRecipientType === 'both';

      let resp;
      if (useRouting) {
        resp = await embedApi.post('/api/packets/send', {
          packet_id: packetId,
          placement: {
            bullhorn_id: entityId,
            placement_id: entityId,
            candidate: {
              entity_type: 'candidate',
              bullhorn_id: entityInfo.candidate.bullhorn_id,
              name: entityInfo.candidate.name,
              email: candEmail,
            },
            contact: {
              entity_type: 'contact',
              bullhorn_id: entityInfo.contact.bullhorn_id,
              name: entityInfo.contact.name,
              email: contactEmail,
            },
          },
          message: customMessage || undefined,
        });
      } else if (entityType === 'placement' && placementRecipientType === 'contact' && contactEmail) {
        resp = await embedApi.post('/api/packets/send', {
          packet_id: packetId,
          recipient: {
            entity_type: 'placement',
            placement_recipient_type: 'contact',
            bullhorn_id: entityInfo?.contact?.bullhorn_id || entityId,
            name: entityInfo?.contact?.name || 'Client Contact',
            email: contactEmail,
            company_id: entityInfo?.company?.bullhorn_id,
            company_name: entityInfo?.company?.name,
            placement_id: entityId,
          },
          message: customMessage || undefined,
        });
      } else {
        const candBhId = entityInfo?.candidate?.bullhorn_id || entityId;
        resp = await embedApi.post('/api/packets/send', {
          packet_id: packetId,
          recipient: {
            entity_type: entityType,
            placement_recipient_type: entityType === 'placement' ? 'candidate' : undefined,
            bullhorn_id: candBhId,
            name: entityInfo?.candidate?.name || entityInfo?.name || 'Unknown',
            email: recipientEmail,
            placement_id: entityType === 'placement' ? entityId : undefined,
          },
          message: customMessage || undefined,
        });
      }

        totalDocs += packetInfo?.template_ids?.length || 0;
        ok += 1;
      } // end for-loop over selectedPackets

      if (ok > 0) {
        toast.success(ok === 1
          ? `Packet sent! (${totalDocs} documents)`
          : `${ok} packets sent! (${totalDocs} total documents)`
        );
      }

      setShowSendDialog(false);
      setSelectedPackets([]);
      setCustomMessage('');
      setEditingEmail(false);
      loadData();

      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: null,
        context: {
          template_name: selectedPackets.length === 1 ? (packets.find(p => p.id === selectedPackets[0])?.name || 'packet') : `${ok} packets`,
          document_type: `${totalDocs} document${totalDocs === 1 ? '' : 's'}`,
          form_name: null,
          recipient_name: entityInfo?.name || 'recipient',
          recipient_email: recipientEmail,
          sender_name: '',
        },
      });
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send packet';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendForm = async () => {
    if (!selectedForm || !entityId) return;

    // Resolve recipient based on entity type — placements default to
    // candidate side here (forms can't yet be sent to both sides; if a
    // recruiter wants that, they can send the form twice, one per side
    // from the Forms workspace). For candidates/contacts we use the
    // entity's primary email.
    let recipientName, recipientAddr;
    if (entityType === 'placement') {
      recipientName = entityInfo?.candidate?.name || entityInfo?.name || 'Candidate';
      recipientAddr = recipientEmail
        || entityInfo?.candidate?.email
        || entityInfo?.email
        || '';
    } else {
      recipientName = entityInfo?.name || 'Unknown';
      recipientAddr = recipientEmail || entityInfo?.email || '';
    }

    if (!recipientAddr) {
      toast.error('This record has no email in Bullhorn — add one first.');
      return;
    }

    setSending(true);
    try {
      const form = forms.find(f => f.id === selectedForm);
      const resp = await embedApi.post('/api/forms/send', {
        form_id: selectedForm,
        bullhorn_id: String(entityId),
        entity_type: form?.entity_type || entityType,
        candidate_name: recipientName,
        candidate_email: recipientAddr,
        expiry_hours: 168,
        message: customMessage || undefined,
      });

      const prefilled = resp.data?.prefilled_count || 0;
      toast.success(
        prefilled > 0
          ? `Form sent! Pre-filled ${prefilled} field${prefilled === 1 ? '' : 's'} from Bullhorn.`
          : 'Form sent successfully!'
      );
      setShowSendDialog(false);
      setSelectedForm('');
      setCustomMessage('');
      setEditingEmail(false);
      loadData();

      // Mirror the document flow — log a note against the record.
      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: null, // Forms don't create a doc_id; the note is informational only
        context: {
          template_name: null,
          form_name: form?.name || 'Untitled',
          document_type: `online form "${form?.name || 'Untitled'}"`,
          recipient_name: recipientName,
          recipient_email: recipientAddr,
          sender_name: '',
        },
      });
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send form';
      if (typeof errorData?.detail === 'string') errorMessage = errorData.detail;
      else if (Array.isArray(errorData?.detail)) errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      else if (errorData?.message) errorMessage = errorData.message;
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (sendMode === 'document') {
      handleSendDocument();
    } else if (sendMode === 'packet') {
      handleSendPacket();
    } else if (sendMode === 'form') {
      handleSendForm();
    }
  };

  const handleCancelDocument = async (docId) => {
    try {
      await embedApi.delete(`/api/documents/${docId}`);
      toast.success('Document cancelled');
      loadData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to cancel document';
      toast.error(errorMsg);
    }
  };

  // ── Credential Request handlers ─────────────────────────────────────
  const openCredentialDialog = async () => {
    setShowCredentialDialog(true);
    setSelectedCredentialIds([]);
    setCredentialCustomMessage('');
    setCredentialSearch('');
    setCredentialRequiredMode('all');
    if (credentialCategories.length === 0) {
      setCredentialCategoriesLoading(true);
      try {
        const r = await embedApi.get('/api/credential-requests/bullhorn-categories');
        setCredentialCategories(r.data?.categories || []);
      } catch (e) {
        toast.error('Could not load credential categories from Bullhorn');
      } finally {
        setCredentialCategoriesLoading(false);
      }
    }
  };

  const handleSendCredentialRequest = async () => {
    if (!recipientEmail) {
      toast.error('Recipient email is required');
      return;
    }
    if (selectedCredentialIds.length === 0) {
      toast.error('Pick at least one credential to request');
      return;
    }
    setCredentialSending(true);
    try {
      const items = selectedCredentialIds.map(id => {
        const cat = credentialCategories.find(c => c.id === id);
        return {
          bullhorn_category_id: id,
          bullhorn_category_name: cat?.name || `Credential #${id}`,
          required: credentialRequiredMode === 'all',
        };
      });

      await embedApi.post('/api/credential-requests', {
        candidate_email: recipientEmail,
        candidate_name: entityInfo?.name || null,
        candidate_bullhorn_id:
          entityType === 'candidate'
            ? parseInt(entityId, 10)
            : entityInfo?.candidate?.id || entityInfo?.candidateId || null,
        entity_type: entityType,
        entity_id: entityId ? parseInt(entityId, 10) : null,
        items,
        custom_message: credentialCustomMessage || null,
      });

      toast.success(`Credential request sent — ${items.length} item${items.length === 1 ? '' : 's'}`);
      setShowCredentialDialog(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send credential request');
    } finally {
      setCredentialSending(false);
    }
  };

  const handleCancelCredentialRequest = async (req) => {
    if (!window.confirm(`Cancel this credential request? The candidate's link will stop working.`)) return;
    try {
      await embedApi.post(`/api/credential-requests/${req.id}/cancel`);
      toast.success('Request cancelled');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleRemindCredentialRequest = async (req) => {
    try {
      await embedApi.post(`/api/credential-requests/${req.id}/remind`);
      toast.success('Reminder sent');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleResendReminder = async (doc) => {
    try {
      await embedApi.post('/api/recipients/send-reminder', {
        recipient_email: doc.candidate_email,
        document_ids: [doc.id]
      });
      toast.success('Reminder sent!');
      loadData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to send reminder';
      toast.error(errorMsg);
    }
  };

  const handleResendCredentials = async (email) => {
    try {
      // Get tenant_id from current user context
      const meResp = await embedApi.get('/api/auth/me');
      const tId = meResp.data?.tenant_id;
      await embedApi.post('/api/portal/auth/resend-credentials', {
        email,
        tenant_id: tId,
      });
      toast.success(`New login details sent to ${email}`);
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to resend login details';
      toast.error(errorMsg);
    }
  };

  // Bulk-fire a reminder for every pending doc grouped by recipient email.
  // The backend's /api/recipients/send-reminder takes a list of document
  // IDs per call, so we batch by `candidate_email` to keep emails per
  // candidate single (one digest email instead of N).
  const handleRemindAllPending = async () => {
    const pendingDocs = documents.filter(d => ['sent', 'opened', 'in_progress'].includes(d.status));
    if (pendingDocs.length === 0) {
      toast.info('No pending documents to remind');
      return;
    }
    const grouped = pendingDocs.reduce((acc, d) => {
      const k = d.candidate_email || 'unknown';
      (acc[k] = acc[k] || []).push(d.id);
      return acc;
    }, {});
    setBulkReminding(true);
    try {
      let ok = 0;
      let failed = 0;
      for (const [email, ids] of Object.entries(grouped)) {
        if (!email || email === 'unknown') {
          failed += ids.length;
          continue;
        }
        try {
          await embedApi.post('/api/recipients/send-reminder', {
            recipient_email: email,
            document_ids: ids,
          });
          ok += ids.length;
        } catch (e) {
          failed += ids.length;
        }
      }
      if (ok > 0 && failed === 0) toast.success(`Reminder sent for ${ok} document${ok === 1 ? '' : 's'}`);
      else if (ok > 0) toast.warning(`Sent ${ok}, ${failed} failed`);
      else toast.error('All reminders failed');
      loadData();
    } finally {
      setBulkReminding(false);
    }
  };

  // Copy the candidate-facing short URL to the clipboard so the
  // recruiter can paste it into Teams / WhatsApp when the email
  // bounces or sits unread.
  const handleCopyLink = async (doc) => {
    const url = doc.share_url;
    if (!url) {
      toast.error('No share link available yet — try sending a reminder to refresh');
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers / restrictive iframes
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied');
    } catch (e) {
      toast.error('Copy blocked by browser — long-press the link in a new tab');
    }
  };

  // Deep-link the recruiter into HyreDocs proper for full tracking /
  // audit. Opens in a new tab because the Bullhorn embed iframe is
  // intentionally narrow.
  const handleOpenInHyreDocs = (doc) => {
    const slug =
      (typeof window !== 'undefined' &&
        (localStorage.getItem('tenantSlug') || sessionStorage.getItem('tenantSlug'))) ||
      'default';
    const url = `${API_URL.replace(/\/$/, '')}/${slug}/documents?status=${doc.status || 'sent'}&doc=${doc.id}`;
    window.open(url, '_blank', 'noopener');
  };

  // Preview document — opens the same PDF the candidate sees in a new tab.
  // For completed docs this is the signed PDF + audit certificate; for sent
  // docs it falls back to the original template PDF.
  const handlePreview = (doc) => {
    const url = doc.completed_document_url || doc.template_file_url || doc.file_url;
    if (!url) {
      toast.error('No preview available yet');
      return;
    }
    window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, '_blank');
  };

  // Stats
  const stats = {
    total: documents.length,
    pending: documents.filter(d => ['sent', 'opened', 'in_progress'].includes(d.status)).length,
    completed: documents.filter(d => ['completed', 'approved', 'pending_approval'].includes(d.status)).length,
  };

  // Apply the active status-chip filter. "all" leaves the list untouched.
  // "pending" / "opened" / "completed" buckets mirror the chip labels and
  // are intentionally generous (e.g. "completed" includes pending-approval
  // and approved) so a recruiter doesn't see a chip that hides matches.
  const filteredDocuments = documents.filter(d => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return ['sent', 'in_progress'].includes(d.status);
    if (statusFilter === 'opened') return d.status === 'opened';
    if (statusFilter === 'completed') return ['completed', 'approved', 'pending_approval'].includes(d.status);
    return true;
  });

  // Update page title + notify Bullhorn parent window with pending count
  useEffect(() => {
    const pendingCount = stats.pending;
    const entityName = entityInfo?.name || `${entityType} #${entityId}`;
    
    // Update browser tab title
    document.title = pendingCount > 0
      ? `(${pendingCount}) ${entityName} - HyreDocs`
      : `${entityName} - HyreDocs`;
    
    // Notify parent (Bullhorn) window so it can render a badge if it listens
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'hyredocs:document-counts',
          entityId,
          entityType,
          total: stats.total,
          pending: stats.pending,
          completed: stats.completed,
        }, '*');
      }
    } catch (e) {
      // postMessage failures are non-fatal
    }
  }, [stats.pending, stats.total, stats.completed, entityId, entityType, entityInfo]);

  // No entity ID provided
  if (!entityId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-700">No Entity Selected</h2>
          <p className="text-sm text-slate-500 mt-1">
            This page should be loaded with an entity ID parameter.
          </p>
          <p className="text-xs text-slate-400 mt-2 font-mono">
            ?entityId=12345&entityType=candidate
          </p>
        </div>
      </div>
    );
  }

  // SSO failed to identify the specific Bullhorn user — surface a clear,
  // actionable "not provisioned" state rather than silently logging the
  // recruiter in as another HyreDocs user. The admin must add a HyreDocs
  // account that matches their Bullhorn email before they can proceed.
  if (ssoError === 'not_provisioned' && !authToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="sso-not-provisioned-screen">
        <div className="max-w-md w-full bg-white rounded-xl border border-amber-200 shadow-sm p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">HyreDocs account not found</h1>
          <p className="text-sm text-slate-600 mb-4">
            Your Bullhorn account isn&apos;t yet linked to a HyreDocs user, so we can&apos;t sign you in automatically.
          </p>
          <p className="text-sm text-slate-600 mb-6">
            Please ask your workspace admin to add your Bullhorn email to HyreDocs, then reload this tab.
          </p>
          {ssoErrorUserId && (
            <p className="text-xs text-slate-400 font-mono mb-4">Bullhorn UserID: {ssoErrorUserId}</p>
          )}
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            data-testid="sso-retry-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // No auth - show inline login form
  if (!authToken) {
    return <InlineLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                entityType === 'candidate' ? 'bg-green-100' : entityType === 'placement' ? 'bg-[#1a4a5e]/10' : 'bg-amber-100'
              }`}>
                {entityType === 'candidate' ? (
                  <User className="w-4 h-4 text-green-600" />
                ) : entityType === 'placement' ? (
                  <FileCheck className="w-4 h-4 text-[#1a4a5e]" />
                ) : (
                  <Building2 className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <div>
                <h1 className="font-semibold text-sm" data-testid="embed-entity-name">
                  {entityInfo?.name || `${(ENTITY_COLORS[entityType]?.label || entityType)} #${entityId}`}
                </h1>
                {/* Show job title for placements */}
                {entityType === 'placement' && entityInfo?.job_title && (
                  <p className="text-xs text-muted-foreground">
                    {entityInfo.job_title}
                    {entityInfo.company_name && ` @ ${entityInfo.company_name}`}
                  </p>
                )}
                {entityInfo?.email && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {entityInfo.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Pending count badge — red to grab attention */}
              {stats.pending > 0 && (
                <Badge className="bg-red-500 hover:bg-red-500 text-white text-xs" data-testid="embed-pending-badge">
                  {stats.pending} Pending
                </Badge>
              )}
              {/* Entity type badge */}
              <Badge 
                className={`${ENTITY_COLORS[entityType]?.bg} ${ENTITY_COLORS[entityType]?.text} text-xs`}
              >
                {ENTITY_COLORS[entityType]?.label || entityType}
              </Badge>
              <Button 
                size="sm" 
                onClick={() => setShowSendDialog(true)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Send className="w-4 h-4 mr-1" />
                Send
              </Button>
              <Button
                size="sm"
                onClick={openCredentialDialog}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="embed-request-credentials-btn"
                title="Request credentials (no PDF needed) — uploads write to the candidate's Bullhorn Credentials tab"
              >
                <ShieldPlus className="w-4 h-4 mr-1" />
                Request Credentials
              </Button>
              {credasEnabled && (entityType === 'candidate' || entityType === 'placement') && (
                <Button
                  size="sm"
                  onClick={handleSendCredas}
                  variant="outline"
                  disabled={credasSending}
                  className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                  data-testid="embed-send-credas-btn"
                  title="Create a Credas verification process for this candidate. Bypasses Credas's own email — generates a magic link you can embed in your own onboarding emails via {{credasLink}}."
                >
                  {credasSending ? (
                    <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-1" /> Send to Credas</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Credas magic-link badge — appears once a Credas process
              exists for this candidate. Click "Copy link" to grab the
              URL for any email, or trust the {{credasLink}} merge tag
              to inject it automatically on the next packet send. */}
          {credasEnabled && credasState?.magic_link && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="credas-link-badge">
              <ShieldCheck className="w-4 h-4 text-indigo-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-indigo-900">
                  Credas link ready
                  <span className="ml-2 text-[10px] font-normal uppercase tracking-wider px-1.5 py-0.5 bg-indigo-100 rounded">
                    {credasState.status || 'pending'}
                  </span>
                </div>
                <code className="text-[11px] text-indigo-700 font-mono truncate block">
                  {credasState.magic_link}
                </code>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-indigo-700 hover:bg-indigo-100"
                onClick={() => {
                  navigator.clipboard.writeText(credasState.magic_link);
                  toast.success('Credas link copied');
                }}
                data-testid="credas-copy-link-btn"
              >
                <CopyIcon className="w-3.5 h-3.5 mr-1" /> Copy link
              </Button>
            </div>
          )}
          {credasEnabled && credasState?.status === 'credas_failed' && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="credas-error-badge">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <div className="flex-1 text-xs text-red-700">
                Credas failed: {credasState.last_error || 'unknown'} — retry from the button above.
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="font-medium">{stats.total} Total</span>
            <span className="flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" />
              {stats.completed} Complete
            </span>
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="w-3 h-3" />
              {stats.pending} Pending
            </span>
            {stats.pending > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemindAllPending}
                disabled={bulkReminding}
                className="h-6 px-2 text-xs"
                data-testid="embed-remind-all-btn"
                title={`Send a reminder email to every candidate with pending documents (${stats.pending})`}
              >
                {bulkReminding ? (
                  <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                ) : (
                  <><BellRing className="w-3 h-3 mr-1" /> Remind all ({stats.pending})</>
                )}
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadData}
              className="ml-auto h-6 px-2"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>

          {/* Status filter chips */}
          {documents.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="embed-status-chips">
              {[
                { v: 'all', label: `All (${stats.total})` },
                { v: 'pending', label: `Pending (${documents.filter(d => ['sent','in_progress'].includes(d.status)).length})` },
                { v: 'opened', label: `Opened (${documents.filter(d => d.status === 'opened').length})` },
                { v: 'completed', label: `Completed (${stats.completed})` },
              ].map(chip => (
                <button
                  key={chip.v}
                  type="button"
                  onClick={() => setStatusFilter(chip.v)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    statusFilter === chip.v
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`embed-status-chip-${chip.v}`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document List */}
      <div className="p-4 space-y-2">
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No documents sent yet</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={() => setShowSendDialog(true)}
            >
              <Send className="w-4 h-4 mr-2" />
              Send First Document
            </Button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-10" data-testid="embed-filtered-empty">
            <p className="text-sm text-slate-500">No documents match this filter.</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setStatusFilter('all')}>
              Show all
            </Button>
          </div>
        ) : (
          filteredDocuments.map((doc) => {
            const isCredentialRequest = doc.kind === 'credential_request';
            // Map a credential request's own statuses ("sent" | "completed"
            // | "cancelled") onto the same chip palette the document rows
            // use so filters & badges stay consistent across both kinds.
            const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.sent;
            const StatusIcon = isCredentialRequest ? Shield : status.icon;
            const isExpanded = expandedDoc === doc.id;
            const isPending = !isCredentialRequest
              ? ['sent', 'opened', 'in_progress'].includes(doc.status)
              : doc.status === 'sent';

            return (
              <div 
                key={doc.id}
                className="bg-white rounded-lg border shadow-sm overflow-hidden"
              >
                <div 
                  className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                >
                  <div className={`w-8 h-8 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-4 h-4 ${status.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-sm truncate">{doc.template_name}</p>
                      {isCredentialRequest && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-emerald-100 text-emerald-800"
                          title="Credential upload request — writes to Bullhorn Credentials tab"
                        >
                          Credentials
                        </span>
                      )}
                      {/* Placement docs: show the recipient side so the
                          recruiter can see at-a-glance whether they emailed
                          the candidate or the contact for this placement. */}
                      {doc.entity_type === 'placement' && doc.placement_recipient_type && (
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${
                            doc.placement_recipient_type === 'contact'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                          title={`Sent to ${doc.placement_recipient_type}`}
                        >
                          → {doc.placement_recipient_type === 'contact' ? 'Contact' : 'Candidate'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {doc.sent_at ? formatDistanceToNow(new Date(doc.sent_at), { addSuffix: true }) : 'Unknown'}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${status.bg} ${status.color} border-0 text-xs`}>
                    {status.label}
                  </Badge>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t bg-slate-50">
                    <div className="grid grid-cols-2 gap-2 py-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Sent:</span>
                        <span className="ml-1 font-medium">
                          {doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy h:mm a') : '-'}
                        </span>
                      </div>
                      {doc.opened_at && (
                        <div>
                          <span className="text-muted-foreground">Opened:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.opened_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                      )}
                      {doc.completed_at && (
                        <div>
                          <span className="text-muted-foreground">Completed:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.completed_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                      )}
                      {doc.expires_at && isPending && (
                        <div>
                          <span className="text-muted-foreground">Expires:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.expires_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                      {/* Reminder schedule hint — kills the "did we chase
                          them yet?" question with one glance. */}
                      {isPending && (doc.reminder_count || doc.last_reminder_at) && (
                        <div className="col-span-2 flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                          <BellRing className="w-3 h-3" />
                          <span>
                            {doc.reminder_count || 0} reminder{(doc.reminder_count || 0) === 1 ? '' : 's'} sent
                            {doc.last_reminder_at && (
                              <> · last {formatDistanceToNow(new Date(doc.last_reminder_at), { addSuffix: true })}</>
                            )}
                          </span>
                        </div>
                      )}
                      {/* Audit summary on completed docs — surfaces the
                          signature certificate and Bullhorn sync state so
                          the recruiter doesn't need to deep-link out. */}
                      {!isPending && doc.completed_at && (
                        <div className="col-span-2 flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                          <ShieldCheck className="w-3 h-3" />
                          <span>
                            Signed &amp; audited
                            {doc.bullhorn_file_attachment_id && <> · uploaded to Bullhorn Files</>}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {isPending && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {!isCredentialRequest && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(doc);
                            }}
                            data-testid={`embed-preview-${doc.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCredentialRequest) {
                              handleRemindCredentialRequest(doc);
                            } else {
                              handleResendReminder(doc);
                            }
                          }}
                          data-testid={`embed-reminder-${doc.id}`}
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Send Reminder
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendCredentials(doc.candidate_email);
                          }}
                          disabled={!doc.candidate_email}
                          data-testid={`embed-resend-login-${doc.id}`}
                        >
                          <KeyRound className="w-3 h-3 mr-1" />
                          Resend Login
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyLink(doc);
                          }}
                          disabled={!doc.share_url}
                          title={doc.share_url ? 'Copy the candidate completion link' : 'No share link yet — try sending a reminder'}
                          data-testid={`embed-copylink-${doc.id}`}
                        >
                          <CopyIcon className="w-3 h-3 mr-1" />
                          Copy link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInHyreDocs(doc);
                          }}
                          title="Open the full tracking page in HyreDocs"
                          data-testid={`embed-open-hyredocs-${doc.id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open in HyreDocs
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs text-red-600 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCredentialRequest) {
                              handleCancelCredentialRequest(doc);
                            } else {
                              handleCancelDocument(doc.id);
                            }
                          }}
                          data-testid={`embed-cancel-${doc.id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Completed document link */}
                    {doc.completed_document_url && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${API_URL}${doc.completed_document_url}`, '_blank');
                          }}
                          data-testid={`embed-view-completed-${doc.id}`}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          View signed PDF &amp; audit certificate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInHyreDocs(doc);
                          }}
                          data-testid={`embed-open-hyredocs-completed-${doc.id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open in HyreDocs
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Send Document/Packet Dialog */}
      <Dialog open={showSendDialog} onOpenChange={(open) => {
        setShowSendDialog(open);
        if (!open) setEditingEmail(false);
      }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Send to {entityInfo?.name || 'Recipient'}</span>
              {entityType && (
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${ENTITY_COLORS[entityType]?.bg || 'bg-slate-500'} ${ENTITY_COLORS[entityType]?.text || 'text-white'}`}>
                  {ENTITY_COLORS[entityType]?.label || entityType}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={sendMode} onValueChange={setSendMode} className="w-full">
            <TabsList className={`grid w-full ${formsEnabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="document" className="flex items-center gap-1.5" data-testid="embed-tab-document">
                <FileText className="w-4 h-4" />
                Document
              </TabsTrigger>
              <TabsTrigger value="packet" className="flex items-center gap-1.5" data-testid="embed-tab-packet">
                <Package className="w-4 h-4" />
                Packet
              </TabsTrigger>
              {formsEnabled && (
                <TabsTrigger value="form" className="flex items-center gap-1.5" data-testid="embed-tab-form">
                  <ClipboardEdit className="w-4 h-4" />
                  Form
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── Document picker ────────────────────────────────────── */}
            <TabsContent value="document" className="space-y-3 mt-4">
              {(() => {
                const eligible = templates
                  .filter(t => t.entity_type === entityType || !t.entity_type || t.entity_type === '')
                  .filter(t => !templateSearch || (t.name || '').toLowerCase().includes(templateSearch.toLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                return (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <Label className="text-sm font-medium flex-shrink-0">Select Template</Label>
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          placeholder="Search templates…"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          data-testid="embed-template-search"
                        />
                      </div>
                    </div>

                    {eligible.length === 0 ? (
                      <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                        <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-600">
                          {templateSearch
                            ? `No templates match "${templateSearch}".`
                            : `No templates available for ${entityType}s.`}
                        </p>
                        {!templateSearch && (
                          <p className="text-xs text-amber-600 mt-1">
                            Create a {entityType} template in Templates page.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-template-list">
                        {eligible.map((t) => {
                          // Determine badge based on routing flags, not entity_type
                          const isContactOnly = !!t.send_to_contact && t.send_to_candidate === false;
                          const isCandidateOnly = t.send_to_candidate !== false && !t.send_to_contact;
                          const ec = isContactOnly
                            ? ENTITY_COLORS.contact
                            : isCandidateOnly
                              ? ENTITY_COLORS.candidate
                              : ENTITY_COLORS[t.entity_type || entityType] || ENTITY_COLORS.candidate;
                          const isSelected = selectedTemplates.includes(t.id);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => toggleTemplate(t.id)}
                              className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 overflow-hidden ${isSelected ? 'bg-slate-50' : ''}`}
                              data-testid={`embed-template-${t.id}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-sm truncate min-w-0">{t.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                    {ec.label}
                                  </span>
                                  {t.is_informational && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex-shrink-0 whitespace-nowrap" title="Read-only · no required fields">
                                      <Eye className="w-2.5 h-2.5" />
                                      Info
                                    </span>
                                  )}
                                </div>
                                {/* Meta line — field count only. The full
                                    candidate-facing instructions live on the
                                    template itself; surfacing them here was
                                    forcing horizontal overflow inside the
                                    narrow Bullhorn iframe and offered no
                                    value to the recruiter who's only
                                    deciding *which* template to send. */}
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  {t.fields?.length || 0} fields
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground">
                        {eligible.length} template{eligible.length === 1 ? '' : 's'} available
                        {selectedTemplates.length > 0 && ` · ${selectedTemplates.length} selected`}
                      </p>
                      {selectedTemplates.length > 0 && (
                        <button type="button" onClick={() => setSelectedTemplates([])} className="text-[11px] text-red-500 hover:text-red-700 font-medium">
                          Uncheck All
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </TabsContent>

            {/* ── Packet picker ──────────────────────────────────────── */}
            <TabsContent value="packet" className="space-y-3 mt-4">
              {(() => {
                const eligible = packets
                  .filter(p => p.entity_type === entityType || !p.entity_type || p.entity_type === '')
                  .filter(p => !packetSearch || (p.name || '').toLowerCase().includes(packetSearch.toLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                const selectedPacketObj = selectedPackets.length === 1 ? packets.find(p => p.id === selectedPackets[0]) : null;
                return (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <Label className="text-sm font-medium flex-shrink-0">Select Packet</Label>
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          placeholder="Search packets…"
                          value={packetSearch}
                          onChange={(e) => setPacketSearch(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          data-testid="embed-packet-search"
                        />
                      </div>
                    </div>

                    {eligible.length === 0 ? (
                      <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                        <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-600">
                          {packetSearch ? `No packets match "${packetSearch}".` : 'No packets available for this entity type.'}
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[36vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-packet-list">
                        {eligible.map((p) => {
                          const ec = ENTITY_COLORS[p.entity_type || entityType] || ENTITY_COLORS.candidate;
                          const isSelected = selectedPackets.includes(p.id);
                          const docCount = p.template_ids?.length || 0;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => togglePacket(p.id)}
                              className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 ${isSelected ? 'bg-slate-50' : ''}`}
                              data-testid={`embed-packet-${p.id}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{p.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                    {ec.label}
                                  </span>
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                  <Files className="w-3 h-3 inline mr-1" />
                                  {docCount} doc{docCount !== 1 ? 's' : ''}
                                  {p.templates && p.templates.length > 0 && ` · ${p.templates.slice(0, 3).map(t => t.name).join(', ')}${p.templates.length > 3 ? ` +${p.templates.length - 3}` : ''}`}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedPackets.length > 0 && (
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">
                          {selectedPackets.length} packet{selectedPackets.length === 1 ? '' : 's'} selected
                        </p>
                        <button type="button" onClick={() => setSelectedPackets([])} className="text-[11px] text-red-500 hover:text-red-700 font-medium">
                          Uncheck All
                        </button>
                      </div>
                    )}

                    {selectedPacketObj && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5 mb-2">
                          <Layers className="w-3.5 h-3.5" />
                          Documents in &ldquo;{selectedPacketObj.name}&rdquo;:
                        </p>
                        <ul className="space-y-1">
                          {selectedPacketObj.templates?.map((t, i) => (
                            <li key={t.id || i} className="flex items-center gap-1.5 text-xs text-slate-600">
                              <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{t.name}</span>
                            </li>
                          )) || (
                            <li className="text-xs text-slate-400">
                              {selectedPacketObj.template_ids?.length || 0} document(s)
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* ── Form picker ────────────────────────────────────────── */}
            {formsEnabled && (
              <TabsContent value="form" className="space-y-3 mt-4" data-testid="embed-form-tab-content">
                {(() => {
                  const eligible = (forms || [])
                    .filter(f => !formSearch || (f.name || '').toLowerCase().includes(formSearch.toLowerCase()))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                  const selectedFormObj = forms.find(x => x.id === selectedForm);
                  const mappedCount = selectedFormObj
                    ? (selectedFormObj.fields || []).filter(fd => fd.bullhorn_source_field && fd.prefill_from_bullhorn !== false).length
                    : 0;
                  return (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-sm font-medium flex-shrink-0">Select Form</Label>
                        <div className="relative flex-1 max-w-xs">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <Input
                            placeholder="Search forms…"
                            value={formSearch}
                            onChange={(e) => setFormSearch(e.target.value)}
                            className="pl-8 h-8 text-sm"
                            data-testid="embed-form-search"
                          />
                        </div>
                      </div>

                      {eligible.length === 0 ? (
                        <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                          <ClipboardEdit className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                          <p className="text-sm text-slate-600">
                            {formSearch ? `No forms match "${formSearch}".` : `No forms available for ${entityType}s.`}
                          </p>
                          {!formSearch && (
                            <p className="text-xs text-amber-600 mt-1">
                              Create one from the Forms workspace.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="max-h-[36vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-form-list">
                          {eligible.map((f) => {
                            const isSelected = selectedForm === f.id;
                            return (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => setSelectedForm(f.id)}
                                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 ${isSelected ? 'bg-slate-50' : ''}`}
                                data-testid={`embed-form-${f.id}`}
                              >
                                <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-violet-600' : 'bg-violet-100'}`}>
                                  <ClipboardEdit className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-violet-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm truncate">{f.name}</span>
                                    {f.status === 'draft' && (
                                      <Badge variant="secondary" className="text-[10px] flex-shrink-0">Draft</Badge>
                                    )}
                                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                                      {f.fields?.length || 0} field{f.fields?.length === 1 ? '' : 's'}
                                    </Badge>
                                  </div>
                                  {f.description && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{f.description}</div>
                                  )}
                                </div>
                                {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {selectedFormObj && (
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="embed-form-preview">
                          <p className="font-medium text-violet-900 flex items-center gap-1.5 text-sm">
                            <ClipboardEdit className="w-4 h-4" />
                            {selectedFormObj.name}
                          </p>
                          <p className="text-xs text-violet-700 mt-1.5">
                            {mappedCount > 0
                              ? `${mappedCount} field${mappedCount === 1 ? '' : 's'} will be pre-filled from this Bullhorn record.`
                              : 'No mapped fields — candidate will fill everything from scratch.'}
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </TabsContent>
            )}
          </Tabs>

          <div className="space-y-4">
            {/* Placement-only: Candidate / Contact / Both recipient toggle */}
            {entityType === 'placement' && entityInfo && (() => {
              const candEmail = entityInfo.candidate?.email || entityInfo.email || '';
              const candName = entityInfo.candidate?.name || entityInfo.name || '';
              const contactEmail = entityInfo.contact?.email || '';
              const contactName = entityInfo.contact?.name || '';
              const hasContact = !!(contactEmail || contactName);
              return (
                <div className="space-y-2" data-testid="embed-placement-recipient-toggle">
                  <Label>Send to</Label>
                  <div className="grid grid-cols-3 gap-1.5 bg-slate-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('candidate')}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'candidate'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : 'text-slate-700 hover:bg-white'
                      }`}
                      data-testid="embed-recipient-candidate"
                    >
                      <div className="font-medium">Candidate</div>
                      <div className={`text-[10px] truncate ${placementRecipientType === 'candidate' ? 'text-white/80' : 'text-slate-500'}`} title={candName}>
                        {candName || '—'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('contact')}
                      disabled={!hasContact}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'contact'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : hasContact
                            ? 'text-slate-700 hover:bg-white'
                            : 'text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="embed-recipient-contact"
                      title={hasContact ? `Send to client contact: ${contactName}` : 'No client contact linked to this placement'}
                    >
                      <div className="font-medium">Contact</div>
                      <div className={`text-[10px] truncate ${placementRecipientType === 'contact' ? 'text-white/80' : 'text-slate-500'}`} title={contactName}>
                        {contactName || 'Not linked'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('both')}
                      disabled={!hasContact || !candEmail}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'both'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : hasContact && candEmail
                            ? 'text-slate-700 hover:bg-white'
                            : 'text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="embed-recipient-both"
                      title="Send one document to each — candidate and contact"
                    >
                      <div className="font-medium">Both</div>
                      <div className={`text-[10px] ${placementRecipientType === 'both' ? 'text-white/80' : 'text-slate-500'}`}>
                        Candidate + Contact
                      </div>
                    </button>
                  </div>
                </div>
              );
            })()}

            {(() => {
              // When sending to "both" on a placement, skip the single-
              // email editor — we use both Bullhorn emails directly and
              // showing one editable input is confusing.
              if (entityType === 'placement' && placementRecipientType === 'both') {
                const candEmail = entityInfo?.candidate?.email || entityInfo?.email || '';
                const contactEmail = entityInfo?.contact?.email || '';
                return (
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2.5 text-sm space-y-1.5" data-testid="embed-both-recipients-summary">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                        <span className="font-medium text-emerald-900 truncate">{candEmail || '(no candidate email)'}</span>
                        <span className="text-[10px] text-emerald-700 ml-auto bg-emerald-100 px-1.5 py-0.5 rounded">Candidate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                        <span className="font-medium text-emerald-900 truncate">{contactEmail || '(no contact email)'}</span>
                        <span className="text-[10px] text-emerald-700 ml-auto bg-emerald-100 px-1.5 py-0.5 rounded">Contact</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">One document will be sent to each — the candidate gets the worker-facing copy, the contact gets the client-facing copy.</p>
                  </div>
                );
              }

              // When Bullhorn gives us a primary email for the record, default to
              // sending there with a tiny "use a different email" toggle. The
              // recruiter never has to type it.
              const entityEmail = entityType === 'placement' && placementRecipientType === 'contact'
                ? (entityInfo?.contact?.email || '')
                : (entityInfo?.candidate?.email || entityInfo?.email || '');
              const usingEntityEmail = entityEmail && recipientEmail.toLowerCase() === entityEmail.toLowerCase();
              const showEditor = editingEmail || !entityEmail;

              if (!showEditor) {
                return (
                  <div className="space-y-2">
                    <Label>Recipient Email</Label>
                    <div className="flex items-center gap-2 rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2.5 text-sm" data-testid="embed-auto-email-row">
                      <Mail className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-emerald-900 truncate" data-testid="embed-auto-email-value">
                          {entityEmail}
                        </p>
                        <p className="text-xs text-emerald-700">
                          Pulled from {entityType === 'placement'
                            ? (placementRecipientType === 'contact' ? "client contact's primary email" : "candidate's primary email")
                            : 'primary email on Bullhorn record'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 flex-shrink-0"
                        onClick={() => setEditingEmail(true)}
                        data-testid="embed-change-email-btn"
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Recipient Email <span className="text-red-500">*</span></Label>
                    {entityEmail && !usingEntityEmail && (
                      <button
                        type="button"
                        className="text-xs text-emerald-700 hover:underline"
                        onClick={() => { setRecipientEmail(entityEmail); setEditingEmail(false); }}
                        data-testid="embed-reset-email-btn"
                      >
                        Use Bullhorn email
                      </button>
                    )}
                  </div>
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="candidate@email.com"
                    required
                    data-testid="embed-recipient-email-input"
                  />
                  {!recipientEmail && (
                    <p className="text-xs text-amber-600">Email is required to send documents</p>
                  )}
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal message to the email..."
                rows={3}
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium">Sending to:</p>
              <p className="text-muted-foreground">
                {entityInfo?.name || `${(ENTITY_COLORS[entityType]?.label || entityType)} #${entityId}`}
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSend}
                disabled={
                  (sendMode === 'document' && selectedTemplates.length === 0) || 
                  (sendMode === 'packet' && selectedPackets.length === 0) || 
                  (sendMode === 'form' && !selectedForm) ||
                  !recipientEmail || 
                  sending
                }
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="embed-send-btn"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendMode === 'document' && selectedTemplates.length > 1
                  ? `Send ${selectedTemplates.length} Documents`
                  : sendMode === 'packet' && selectedPackets.length > 1
                    ? `Send ${selectedPackets.length} Packets`
                    : `Send ${sendMode === 'packet' ? 'Packet' : sendMode === 'form' ? 'Form' : 'Document'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BullhornNotePromptDialog
        {...bullhornNote.popup}
        apiClient={embedApi}
      />

      {/* Credential Request dialog */}
      <Dialog open={showCredentialDialog} onOpenChange={setShowCredentialDialog}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              Request Credentials
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Pick the credentials this candidate needs to upload. Each item will land on their Bullhorn Credentials tab.
              No PDF needed — they get a streamlined upload page.
            </p>
          </DialogHeader>

          <div className="space-y-4 pt-2 min-w-0">
            {/* Recipient */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Send to</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="candidate@example.com"
                data-testid="credreq-recipient-email"
              />
              {entityInfo?.name && (
                <p className="text-xs text-muted-foreground">{entityInfo.name}</p>
              )}
            </div>

            {/* Required mode */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Items are…</Label>
              <div className="flex gap-2">
                {[
                  { v: 'all', label: 'All required' },
                  { v: 'optional', label: 'All optional' },
                ].map(opt => (
                  <Button
                    key={opt.v}
                    type="button"
                    size="sm"
                    variant={credentialRequiredMode === opt.v ? 'default' : 'outline'}
                    onClick={() => setCredentialRequiredMode(opt.v)}
                    data-testid={`credreq-required-${opt.v}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Category picker */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Credentials ({selectedCredentialIds.length} selected)
                </Label>
                {selectedCredentialIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCredentialIds([])}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </button>
                )}
              </div>
              <Input
                placeholder="Search Bullhorn categories…"
                value={credentialSearch}
                onChange={(e) => setCredentialSearch(e.target.value)}
                data-testid="credreq-search"
              />
              <div className="border rounded-md max-h-80 overflow-y-auto bg-slate-50">
                {credentialCategoriesLoading ? (
                  <p className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Loading from Bullhorn…
                  </p>
                ) : credentialCategories.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">
                    No credential categories available. Check Bullhorn connection.
                  </p>
                ) : (
                  credentialCategories
                    .filter(c => !credentialSearch || c.name.toLowerCase().includes(credentialSearch.toLowerCase()))
                    .slice(0, 2000)
                    .map(c => {
                      const checked = selectedCredentialIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b last:border-b-0 hover:bg-white min-w-0 ${checked ? 'bg-emerald-50' : ''}`}
                          data-testid={`credreq-cat-${c.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCredentialIds([...selectedCredentialIds, c.id]);
                              } else {
                                setSelectedCredentialIds(selectedCredentialIds.filter(id => id !== c.id));
                              }
                            }}
                            className="flex-shrink-0"
                          />
                          <span className="truncate flex-1 min-w-0">{c.name.replace(/^\s+/, '')}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">#{c.id}</span>
                        </label>
                      );
                    })
                )}
              </div>
            </div>

            {/* Custom message */}
            <div className="space-y-1.5 min-w-0">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Custom message (optional)</Label>
              <Textarea
                value={credentialCustomMessage}
                onChange={(e) => setCredentialCustomMessage(e.target.value)}
                placeholder="Hi! Please upload these so we can get your start date confirmed…"
                rows={3}
                className="w-full max-w-full resize-y"
                data-testid="credreq-custom-message"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 mt-2 border-t sticky bottom-0 bg-background -mx-6 px-6 -mb-6 pb-4">
              <Button variant="outline" onClick={() => setShowCredentialDialog(false)} disabled={credentialSending} data-testid="credreq-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSendCredentialRequest}
                disabled={credentialSending || selectedCredentialIds.length === 0 || !recipientEmail}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="credreq-send-btn"
              >
                {credentialSending ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Request ({selectedCredentialIds.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" />
    </div>
  );
}
